import { useEffect, useState, useRef } from 'react';
import { ChevronDown, ChevronUp, Terminal } from 'lucide-react';

/**
 * AuditConsole - Terminal-style display for live audit logs
 * Shows real-time compliance simulation data as the demo progresses
 * Collapsible on mobile for better UX
 */

type SimulationPhase = 'idle' | 'detecting' | 'analyzing' | 'alerting' | 'resolved';

interface AuditConsoleProps {
    phase: SimulationPhase;
}

interface LogEntry {
    timestamp: string;
    prefix: string;
    message: string;
    color: string;
}

const PHASE_LOGS: Record<SimulationPhase, LogEntry[]> = {
    idle: [
        { timestamp: '00:00:00', prefix: 'AUDIT_SYS', message: 'System initialized. Awaiting project file...', color: 'text-gray-400' },
        { timestamp: '00:00:01', prefix: 'READY', message: 'Click "Simulate Inspection" to begin', color: 'text-cyan-400' },
    ],
    detecting: [
        { timestamp: '00:00:02', prefix: 'SCAN_INIT', message: 'Loading floor plan: PROJ_2024_ALPHA.dwg', color: 'text-cyan-400' },
        { timestamp: '00:00:03', prefix: 'ZONE_SCAN', message: 'Analyzing Zone 1: North Wing... [OK]', color: 'text-green-400' },
        { timestamp: '00:00:04', prefix: 'ZONE_SCAN', message: 'Analyzing Zone 2: East Wing... [OK]', color: 'text-green-400' },
        { timestamp: '00:00:05', prefix: 'ZONE_SCAN', message: 'Analyzing Zone 3: South Wing... [ANOMALY]', color: 'text-yellow-400' },
    ],
    analyzing: [
        { timestamp: '00:00:06', prefix: 'AI_CORE', message: 'Cross-referencing NFPA 72 code requirements...', color: 'text-cyan-400' },
        { timestamp: '00:00:07', prefix: 'AI_CORE', message: 'Checking egress path calculations...', color: 'text-cyan-400' },
        { timestamp: '00:00:08', prefix: 'RISK_LVL', message: 'Threat assessment: MEDIUM (0.67)', color: 'text-yellow-400' },
        { timestamp: '00:00:09', prefix: 'VALIDATE', message: 'Anomaly confirmed: Egress width insufficient', color: 'text-orange-400' },
    ],
    alerting: [
        { timestamp: '00:00:10', prefix: 'ALERT', message: '⚠️ NON-COMPLIANCE DETECTED', color: 'text-red-400' },
        { timestamp: '00:00:11', prefix: 'DETAIL', message: 'Zone 3: Corridor width 0.9m < required 1.2m', color: 'text-red-300' },
        { timestamp: '00:00:12', prefix: 'NOTIFY', message: 'Sending report to project lead: arch@studio.com', color: 'text-cyan-400' },
        { timestamp: '00:00:13', prefix: 'REMEDY', message: 'Recommended fix: Increase corridor to 1.2m', color: 'text-yellow-400' },
    ],
    resolved: [
        { timestamp: '00:00:14', prefix: 'UPDATE', message: 'Revised plan submitted. Re-scanning...', color: 'text-cyan-400' },
        { timestamp: '00:00:15', prefix: 'ZONE_SCAN', message: 'Zone 3 re-check: Corridor 1.25m [OK]', color: 'text-green-400' },
        { timestamp: '00:00:16', prefix: 'REPORT', message: 'Compliance report generated: RPT_2024_1234.pdf', color: 'text-green-400' },
        { timestamp: '00:00:17', prefix: 'STATUS', message: '✓ FULL COMPLIANCE - AUDIT PASSED', color: 'text-green-500' },
    ],
};

export function AuditConsole({ phase }: AuditConsoleProps) {
    const [visibleLogs, setVisibleLogs] = useState<LogEntry[]>([]);
    const [logIndex, setLogIndex] = useState(0);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const consoleRef = useRef<HTMLDivElement>(null);

    // Check if mobile
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Reset logs when phase changes
    useEffect(() => {
        setVisibleLogs([]);
        setLogIndex(0);
        // Auto-expand when simulation starts
        if (phase !== 'idle') setIsCollapsed(false);
    }, [phase]);

    // Progressively reveal logs
    useEffect(() => {
        const phaseLogs = PHASE_LOGS[phase];
        if (logIndex >= phaseLogs.length) return;

        const timer = setTimeout(() => {
            setVisibleLogs(prev => [...prev, phaseLogs[logIndex]]);
            setLogIndex(prev => prev + 1);
        }, 200 + Math.random() * 300); // Staggered timing for realism

        return () => clearTimeout(timer);
    }, [phase, logIndex]);

    // Auto-scroll to bottom
    useEffect(() => {
        if (consoleRef.current && !isCollapsed) {
            consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
        }
    }, [visibleLogs, isCollapsed]);

    return (
        <div className="rounded-lg border border-gray-700 bg-gray-950 overflow-hidden font-mono text-xs">
            {/* Terminal Header - Collapsible on mobile */}
            <button
                onClick={() => isMobile && setIsCollapsed(!isCollapsed)}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 bg-gray-900 border-b border-gray-800 ${isMobile ? 'cursor-pointer hover:bg-gray-800' : 'cursor-default'}`}
            >
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    <span className="ml-2 text-gray-500">environaise_audit_console</span>
                </div>
                {isMobile && (
                    <div className="flex items-center gap-1 text-gray-500">
                        <Terminal className="w-4 h-4" />
                        {isCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                    </div>
                )}
            </button>

            {/* Log Display - Collapsible */}
            <div
                ref={consoleRef}
                className={`overflow-hidden transition-all duration-300 ${isCollapsed ? 'h-0' : 'h-48'}`}
            >
                <div className="p-4 h-full overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700">
                    {visibleLogs.map((log, i) => (
                        <div key={i} className="flex gap-2 mb-1">
                            <span className="text-gray-600">[{log.timestamp}]</span>
                            <span className="text-purple-400">{log.prefix}</span>
                            <span className="text-gray-500">&gt;</span>
                            <span className={log.color}>{log.message}</span>
                        </div>
                    ))}
                    {/* Blinking cursor */}
                    <div className="inline-block w-2 h-4 bg-cyan-400 animate-pulse" />
                </div>
            </div>
        </div>
    );
}

export default AuditConsole;
